/**
 * Keskitetty React Query -avaintenhallinta
 * 
 * Käyttö:
 * - Hakuun: queryKey: queryKeys.orders.list()
 * - Invalidointiin: queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
 * 
 * Rakenne noudattaa TanStack Query -dokumentaation suosittelemia käytäntöjä:
 * https://tanstack.com/query/latest/docs/framework/react/community/lukemorales-query-key-factory
 */

// ============ Orders ============

export const queryKeys = {
  // Tilaukset
  orders: {
    all: ["orders"] as const,
    list: () => [...queryKeys.orders.all, "list"] as const,
    detail: (id: string) => [...queryKeys.orders.all, "detail", id] as const,
    stats: () => [...queryKeys.orders.all, "stats"] as const,
    reports: (startDate?: string, endDate?: string) => 
      ["reports-orders", startDate, endDate] as const,
  },

  // Tilausrivit ja konfiguraatiot
  orderLines: {
    all: ["order-lines"] as const,
    byOrder: (orderId: string) => [...queryKeys.orderLines.all, orderId] as const,
  },

  // Workshop / tuotanto
  workshop: {
    all: ["workshop-orders"] as const,
    cuttingList: (date: string) => ["cutting-list", date] as const,
  },

  // Asiakkaat
  customers: {
    all: ["customers"] as const,
    list: () => [...queryKeys.customers.all, "list"] as const,
    detail: (id: string) => [...queryKeys.customers.all, id] as const,
  },

  // Hautausmaat
  cemeteries: {
    all: ["cemeteries"] as const,
    list: () => [...queryKeys.cemeteries.all, "list"] as const,
    detail: (id: string) => [...queryKeys.cemeteries.all, id] as const,
  },

  // Yhteistyökumppanit
  partners: {
    all: ["partners"] as const,
    list: () => [...queryKeys.partners.all, "list"] as const,
    detail: (id: string) => [...queryKeys.partners.all, id] as const,
  },

  // Kuormat
  loads: {
    all: ["installation-loads"] as const,
    list: () => [...queryKeys.loads.all, "list"] as const,
    detail: (id: string) => [...queryKeys.loads.all, id] as const,
    installations: () => ["load-installations"] as const,
    installationsByLoad: (loadId: string) => 
      [...queryKeys.loads.installations(), loadId] as const,
  },

  // Asentajan omat kuormat ja tehtävät
  myLoads: {
    all: ["my-loads"] as const,
    installations: () => ["my-load-installations"] as const,
    claimedPickups: () => ["claimed-pickups"] as const,
    unassignedPickups: () => ["unassigned-pickups"] as const,
  },

  // Asennukset
  installations: {
    all: ["all-installations"] as const,
    list: () => [...queryKeys.installations.all, "list"] as const,
    upcoming: () => ["upcoming-installations"] as const,
    photos: (installationId: string) => ["installation-photos", installationId] as const,
  },

  // Tuotteet
  products: {
    all: ["products"] as const,
    list: () => [...queryKeys.products.all, "list"] as const,
    detail: (id: string) => [...queryKeys.products.all, id] as const,
    materialTemplates: (productId: string) => 
      ["product-material-templates", productId] as const,
    components: (productId: string) => ["product-components", productId] as const,
  },

  // Materiaalit
  materials: {
    all: ["materials"] as const,
    list: () => [...queryKeys.materials.all, "list"] as const,
    detail: (id: string) => [...queryKeys.materials.all, id] as const,
  },

  // Tuotemallit ja konfigurointi
  productModels: {
    all: ["product_models"] as const,
    list: () => [...queryKeys.productModels.all, "list"] as const,
    detail: (id: string) => [...queryKeys.productModels.all, id] as const,
    components: (modelId: string) => ["model_components", modelId] as const,
    withComponents: () => ["product_models_with_components"] as const,
  },

  // Komponenttityypit
  componentTypes: {
    all: ["component_types"] as const,
    list: () => [...queryKeys.componentTypes.all, "list"] as const,
    materials: (componentTypeId: string) => 
      ["component_type_materials", componentTypeId] as const,
    attributeDefs: (componentTypeId: string) => 
      ["component_attribute_defs", componentTypeId] as const,
  },

  // Attribuutit
  attributes: {
    all: ["attributes"] as const,
    list: () => [...queryKeys.attributes.all, "list"] as const,
  },

  // Konfiguraatiot
  configurations: {
    all: ["configurations"] as const,
    detail: (id: string) => [...queryKeys.configurations.all, id] as const,
    components: (configId: string) => ["config-components", configId] as const,
    attributeValues: (configId: string) => ["config-attribute-values", configId] as const,
  },

  // Viestintä
  communication: {
    all: ["communication-logs"] as const,
    byOrder: (orderId?: string) => 
      orderId 
        ? [...queryKeys.communication.all, "order", orderId] as const
        : queryKeys.communication.all,
    byCustomer: (customerId?: string) => 
      customerId 
        ? [...queryKeys.communication.all, "customer", customerId] as const
        : queryKeys.communication.all,
    byOrderAndCustomer: (orderId?: string, customerId?: string) => 
      ["communication-logs", orderId, customerId] as const,
  },

  // Sähköpostimallit
  emailTemplates: {
    all: ["email-templates"] as const,
    list: () => [...queryKeys.emailTemplates.all, "list"] as const,
  },

  // Vedokset (proofs)
  proofs: {
    all: ["order-proofs"] as const,
    byOrder: (orderId: string) => [...queryKeys.proofs.all, orderId] as const,
    cemetery: (orderId: string) => ["cemetery-proofs", orderId] as const,
  },

  // Tiedostot
  files: {
    all: ["order-files"] as const,
    byOrder: (orderId: string) => [...queryKeys.files.all, orderId] as const,
  },

  // Käyttäjät ja profiilit
  users: {
    all: ["users"] as const,
    installers: () => ["installers"] as const,
    profile: (userId: string) => ["profile", userId] as const,
  },

  // API-käyttö ja kiintiöt
  apiUsage: {
    quotas: ["api-quotas"] as const,
    summary: ["api-usage-summary"] as const,
    logs: ["api-usage-logs"] as const,
    dailyUsage: ["api-daily-usage"] as const,
    logsWithParams: (service?: string, status?: string, limit?: number, offset?: number) =>
      ["api-usage-logs", service, status, limit, offset] as const,
    dailyUsageWithDays: (days: number) => ["api-daily-usage", days] as const,
  },

  // Hinnoittelusäännöt
  pricingRules: {
    all: ["price-rules"] as const,
    list: () => [...queryKeys.pricingRules.all, "list"] as const,
  },

  // Kalenteri
  calendar: {
    events: (start?: string, end?: string) => ["calendar-events", start, end] as const,
  },

  // Hakutyökalu (CemeteriesMap, OrdersMap)
  geocoding: {
    search: (query: string) => ["geocoding", query] as const,
  },
} as const;

// Tyyppiapurit
export type QueryKeys = typeof queryKeys;

/**
 * Apufunktio kaikkien avaimen alaisten kyselyiden invalidointiin
 * 
 * Esimerkki:
 * invalidateAllQueries(queryClient, queryKeys.orders.all)
 * -> Invalidoi kaikki orders-alkuiset kyselyt
 */
export function createInvalidator(queryClient: { invalidateQueries: (opts: { queryKey: readonly unknown[] }) => void }) {
  return (key: readonly unknown[]) => {
    queryClient.invalidateQueries({ queryKey: key });
  };
}
